import { environment } from '../../environments/environment';

/**
 * Where the MSAL redirect bridge lives.
 *
 * Derived from the configured redirect URI rather than repeated as a literal:
 * the same path has to be served as a route, recognised by the app shell, and
 * registered on the Entra app registration, and if those drift apart the
 * failure is remote from its cause (login keeps working from config while
 * logout sends an unregistered reply URL, or vice versa).
 */
export const AUTH_BRIDGE_PATH = extractPath(environment.msal.redirectUri);

/** Route-definition form of {@link AUTH_BRIDGE_PATH} — no leading slash. */
export const AUTH_BRIDGE_ROUTE = AUTH_BRIDGE_PATH.replace(/^\//, '');

/**
 * True when the current window was loaded on the bridge page.
 *
 * Compares against the configured path, so a subpath deployment
 * (base href '/app/') matches on '/app/redirect', and a trailing slash added
 * by a hosting rewrite still matches. Auth popups close right after the
 * bridge runs and the redirect flow leaves via a full navigation, so this is
 * settled at load time and never changes during in-app routing.
 */
export function isAuthBridgeWindow(): boolean {
  return normalize(window.location.pathname) === AUTH_BRIDGE_PATH;
}

/**
 * Pulls the path out of a configured redirect URI. Cannot assume a parseable
 * absolute URL: environment.ts carries the unsubstituted '${UI_URL}/redirect'
 * placeholder that the deploy pipeline fills in.
 */
function extractPath(redirectUri: string): string {
  const withoutOrigin = redirectUri
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '')
    .replace(/^\$\{[^}]*\}/, '');
  return normalize(withoutOrigin.split(/[?#]/)[0]);
}

/** Strips trailing slashes and guarantees a single leading one. */
function normalize(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
